import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * usePersistedState — Like useState but persists to sessionStorage.
 *
 * Survives React re-mounts (navigation between pages), page refreshes,
 * and tab restoration. State is scoped per-tab (sessionStorage) so
 * multiple tabs don't interfere.
 *
 * Usage:
 *   const [value, setValue] = usePersistedState('page.camera.zoom', 1.0);
 *   const [tab, setTab] = usePersistedState('intelligence.activeTab', 'overview');
 *
 * For objects, the hook deep-merges the persisted value with the default
 * so new fields added in code updates are always present.
 *
 * @param {string} key - Unique storage key (namespaced, e.g. 'canvas.selectedWorkflow')
 * @param {*} defaultValue - Initial value if nothing persisted
 * @param {object} options - Optional: { serialize, deserialize, debounceMs }
 */
const PREFIX = 'agentlets_ps_';

export function usePersistedState(key, defaultValue, options = {}) {
  const {
    debounceMs = 300,
  } = options;

  const fullKey = PREFIX + key;
  const debounceRef = useRef(null);

  // Initialize from storage or default
  const [value, setValue] = useState(() => {
    try {
      const stored = sessionStorage.getItem(fullKey);
      if (stored === null) return defaultValue;
      const parsed = JSON.parse(stored);
      // Deep-merge objects so new default fields are present
      if (defaultValue && typeof defaultValue === 'object' && !Array.isArray(defaultValue)
          && parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return { ...defaultValue, ...parsed };
      }
      return parsed;
    } catch {
      return defaultValue;
    }
  });

  // Persist on change (debounced to avoid thrashing on rapid updates like camera movement)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      try {
        if (value === undefined || value === null) {
          sessionStorage.removeItem(fullKey);
        } else {
          sessionStorage.setItem(fullKey, JSON.stringify(value));
        }
      } catch {
        // Storage full or unavailable — fail silently
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, fullKey, debounceMs]);

  // Clear helper
  const clearPersistedValue = useCallback(() => {
    sessionStorage.removeItem(fullKey);
    setValue(defaultValue);
  }, [fullKey, defaultValue]);

  return [value, setValue, clearPersistedValue];
}

/**
 * usePersistedRef — Persists a ref value without causing re-renders.
 * Ideal for camera state, scroll positions, and other high-frequency data.
 *
 * Usage:
 *   const cameraRef = usePersistedRef('3d.camera', { x: 0, y: -200, z: 500, ... });
 *   // Read/write cameraRef.current freely — auto-saved on interval
 *   // Call cameraRef.save() to force an immediate save
 */
export function usePersistedRef(key, defaultValue, options = {}) {
  const { saveIntervalMs = 2000 } = options;
  const fullKey = PREFIX + key;

  // Restore or use default
  const initial = (() => {
    try {
      const stored = sessionStorage.getItem(fullKey);
      if (stored === null) return defaultValue;
      const parsed = JSON.parse(stored);
      if (defaultValue && typeof defaultValue === 'object' && !Array.isArray(defaultValue)) {
        return { ...defaultValue, ...parsed };
      }
      return parsed;
    } catch {
      return defaultValue;
    }
  })();

  const ref = useRef(initial);

  // Save to function
  const save = useCallback(() => {
    try {
      sessionStorage.setItem(fullKey, JSON.stringify(ref.current));
    } catch {/* */}
  }, [fullKey]);

  // Periodic auto-save
  useEffect(() => {
    const interval = setInterval(save, saveIntervalMs);
    // Save on unmount
    return () => {
      clearInterval(interval);
      save();
    };
  }, [save, saveIntervalMs]);

  // Attach save method to ref for manual triggers
  ref.save = save;

  return ref;
}

/**
 * clearAllPersistedState — Wipe all persisted page state.
 * Useful on logout.
 */
export function clearAllPersistedState() {
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith(PREFIX)) {
      keysToRemove.push(k);
    }
  }
  keysToRemove.forEach(k => sessionStorage.removeItem(k));
}
